import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, addMonths, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTutorial } from '@/contexts/TutorialContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
// DMC-D3c-1: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { aggregateShopCapacity, resolveTechnicianCapacity } from '@/utils/capacity/technicianCapacityState';
import { CALENDAR_TECHNICIAN_COLUMNS, CALENDAR_WORKLOAD_RPC, dayWorkload } from '@/utils/calendarWorkload';
import { countsAsMechanicalCapacity, sumEffectiveWorkload } from '@/utils/dayEffectiveLoad';
import { useMechanicalRoster, useMechanicalTechnicianIds } from '@/hooks/useMechanicalTechnicianIds';
import { clock } from '@/utils/clock';
import { useTechnicianAbsenceIntervals } from '@/hooks/useTechnicianAbsenceIntervals';
import { withAbsenceIntervals } from '@/utils/capacity/absenceIntervals';
import { getTodayInShopTz } from '@/utils/shopTime';
import { calendarDayStatus, PAST_DAY_NOTE, PROJECTION_NOTE, type ShopHoliday } from '@/lib/calendar/calendarDayStatus';
// Core-100 R4 — CORE-BR Responsibility 6: weather context reaches the scheduling calendar (notice only).
import { SchedulingWeatherAdvisory } from '@/components/weather/SchedulingWeatherAdvisory';
import { TICKET_UPDATED_EVENT } from '@/utils/dayboardInvalidation';
import { calculateOBLI as calculateOBLIScore, formatOBLIScore, getOBLIColorClasses, isOBLICapacityKnown, OBLI_CAPACITY_UNAVAILABLE_LABEL, type OBLIResult } from '@/utils/obliCalculation';

interface CalendarModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDate: Date;
  /**
   * Called with YYYY-MM-DD string (shop-local day label)
   * NOT a Date object to avoid browser timezone issues
   */
  onDateSelect: (dayLabel: string) => void;
}

export const CalendarModal = ({ open, onOpenChange, currentDate, onDateSelect }: CalendarModalProps) => {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const { isActive, currentStepData, nextStep } = useTutorial();
  const [viewMonth, setViewMonth] = useState(new Date());
  const [shopHours, setShopHours] = useState<Record<number, { open: string; close: string }>>({});
  const [holidays, setHolidays] = useState<ShopHoliday[]>([]);
  const [rosterTechnicians, setTechnicians] = useState<any[]>([]);
  // Whose roster `technicians` holds and whether it loaded — capacity is not
  // a measurement until it has (see calculateOBLI's `capacityKnown`).
  const [rosterState, setRosterState] = useState<{ shopId: string | null; status: 'loading' | 'ready' | 'error' }>({ shopId: null, status: 'loading' });
  const [tickets, setTickets] = useState<any[]>([]);
  const [bays, setBays] = useState<any[]>([]);

  // Core-100 R4 (CORE-AW): the visible month's partial-day absences are capacity input — a technician away
  // 10:00-12:00 is not capacity then. Until they are known, capacity is not presented as known.
  const absenceIntervals = useTechnicianAbsenceIntervals(
    open ? activeShop?.id : null,
    format(startOfMonth(viewMonth), 'yyyy-MM-dd'),
    format(endOfMonth(viewMonth), 'yyyy-MM-dd'),
  );
  const technicians = useMemo(
    () => withAbsenceIntervals(rosterTechnicians, absenceIntervals.byTech),
    [rosterTechnicians, absenceIntervals.byTech],
  );

  // Core-100 R4 W5 (CORE-AX Rel): the ticket read is bounded to the visible month (server read model). The range
  // lives in a ref so the "ticket changed" / roster listeners always re-read the month on screen.
  const visibleRange = { from: format(startOfMonth(viewMonth), 'yyyy-MM-dd'), to: format(endOfMonth(viewMonth), 'yyyy-MM-dd') };
  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;

  // ARC-A3: dep tightened from `[open, activeShop]` to
  // `[open, activeShop?.id]`. The effect runs 3+ DB queries
  // (loadShopData + loadTechniciansAndTickets + loadBays); pre-A3 every
  // activeShop reference replacement (refreshActiveShop, AuthProvider
  // re-render pre-ARC-A2) re-fired all three. ID-only is sufficient
  // since the gate is `open && activeShop` (nullness + id presence) and
  // each loader internally reads `activeShop.id`.
  useEffect(() => {
    if (open && activeShop) {
      loadShopData();
      loadTechniciansAndTickets();
      loadBays();
    }
  }, [open, activeShop?.id]);

  // Core-100 R4 W5 (CORE-AX Rel): another month (or a different shop zone) is another bounded read.
  useEffect(() => {
    if (open && activeShop) void loadTechniciansAndTickets();
  }, [visibleRange.from, shopTimezone]);

  // While open, follow the same "a ticket changed" signal the Dayboard does
  // (a Stop / Final Stop / reschedule on this device, or broadcast from
  // another) — the open month used to keep counting work that had finished.
  useEffect(() => {
    if (!open || !activeShop) return undefined;
    const onTicketUpdated = () => { void loadTechniciansAndTickets(); };
    window.addEventListener(TICKET_UPDATED_EVENT, onTicketUpdated);
    return () => window.removeEventListener(TICKET_UPDATED_EVENT, onTicketUpdated);
  }, [open, activeShop?.id]);

  // Core-100 R4 (CORE-AX): a roster change (hours, time off, breaks, efficiency, a technician added or
  // deactivated) while the month is open re-reads the roster — the open month used to keep the capacity it
  // was opened with.
  useEffect(() => {
    if (!open || !activeShop) return undefined;
    const channel = supabase
      .channel(`calendar-roster-${activeShop.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'technicians', filter: `shop_id=eq.${activeShop.id}` }, () => {
        void loadTechniciansAndTickets();
      })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [open, activeShop?.id]);

  const loadShopData = async () => {
    if (!activeShop) return;
    
    const { data } = await supabase
      .from('shop_profile')
      .select('hours, holidays')
      .eq('shop_id', activeShop.id)
      .limit(1)
      .maybeSingle();

    if (data) {
      const hoursData = (data.hours as any) || {};
      const mappedHours: Record<number, { open: string; close: string }> = {};

      // PTP-024 — accept EITHER key casing.
      //
      // This map was capitalized-only, while `shop_profile.hours` is
      // canonically stored lowercase (onboarding's ShopBasicsStep writes
      // lowercase, and `commitWizard` notes "We keep shop_profile.hours in its
      // lowercase form elsewhere"). Every real shop therefore produced an EMPTY
      // `mappedHours`, and the modal believed the shop never opened.
      //
      // Lower-casing the incoming key is the whole fix; it matches how
      // `shopHoursUtils.getShopHoursForDay` already resolves this elsewhere,
      // so no second normalization convention is introduced.
      const dayMap: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6,
      };

      Object.entries(hoursData).forEach(([dayName, hours]: [string, any]) => {
        const dayNum = dayMap[String(dayName).toLowerCase()];
        if (dayNum !== undefined && hours?.open && hours?.close) {
          mappedHours[dayNum] = { open: hours.open, close: hours.close };
        }
      });

      setShopHours(mappedHours);
      setHolidays(((data.holidays as any[]) || []).filter(h => typeof h?.date === 'string').map(h => ({ date: h.date, name: h.name ?? null })));
    }
  };

  const loadTechniciansAndTickets = async () => {
    if (!activeShop) return;

    const { data: techData, error: techError } = await supabase
      .from('technicians')
      // Core-100 R4 (CORE-AX): only what capacity and load read — never pay, contact or profile columns.
      .select(CALENDAR_TECHNICIAN_COLUMNS)
      .eq('shop_id', activeShop.id)
      .eq('employment_status', 'Active');
    setRosterState({ shopId: activeShop.id, status: techError || !techData ? 'error' : 'ready' });
    
    // R125-H-7: narrow select (no customer contact data). The columns are
    // the canonical production fields plus what the Calendar measures, so
    // the day's workload is decided by the shared production truth
    // (calendarWorkload.ts), not by status/parking_lot alone. Terminal
    // facts are excluded at the source; status stays a cheap pre-filter.
    //
    // Core-100 R4 W5 (CORE-AX S/Rel): through the server read model — the caller's RLS, the workload columns only
    // (no customer contact), the visible month only (it read every open ticket of the shop), terminal facts and the
    // status pre-filter applied at the source, the day decided in the shop's zone.
    const range = visibleRangeRef.current;
    const { data: ticketData } = await supabase.rpc(CALENDAR_WORKLOAD_RPC as never, {
      p_shop_id: activeShop.id, p_from: range.from, p_to: range.to, p_time_zone: shopTimezone,
    } as never);

    if (techData) setTechnicians(techData);
    if (ticketData) setTickets(ticketData as unknown as any[]);
  };

  const loadBays = async () => {
    if (!activeShop) return;

    const { data: bayData } = await supabase
      .from('shop_bays')
      .select('*')
      .eq('shop_id', activeShop.id)
      .eq('is_active', true);

    if (bayData) setBays(bayData);
  };

  // CORE-AX: "today" is the SHOP's today, and a holiday is not the same thing as a closed weekday.
  const shopTodayLabel = getTodayInShopTz(shopTimezone);
  const openDays = new Set(Object.keys(shopHours).map(Number));
  const dayStatus = (date: Date) =>
    calendarDayStatus(format(date, 'yyyy-MM-dd'), getDay(date), { holidays, openDays, shopTodayLabel });

  const isShopOpen = (date: Date) => dayStatus(date).kind === 'open';

  /** Effective load ÷ mechanical capacity, in % (capped at 100 unless `cap` is false). */
  const calculateShopUtilization = (date: Date, cap = true) => {
    if (!isShopOpen(date)) return 0;

    const totalCapacity = calculateTotalCapacity(date);
    const effectiveMinutes = getEffectiveMinutes(date);

    if (totalCapacity === 0) return 0;
    const pct = (effectiveMinutes / totalCapacity) * 100;
    return cap ? Math.min(100, pct) : pct;
  };

  // The production workload committed to this shop day — the shared law
  // (calendarWorkload.ts): booked work counts whether or not the car is
  // parked; estimates, finished and deleted work never do. R8-M-3: the day is
  // scheduled_date, else start_time in the SHOP's zone (never a UTC split).
  // DMC-D3c-1: deleted tickets are excluded by the canonical state as well as
  // defensively here (both reps).
  const workloadFor = (date: Date) =>
    dayWorkload(tickets.filter((t) => !isTicketDeleted(t)), format(date, 'yyyy-MM-dd'), shopTimezone);

  const getTicketCount = (date: Date) => workloadFor(date).ticketCount;

  // EFFECTIVE LOAD — the Dayboard's own per-ticket mapping (efficiency /
  // buffer adjusted; parked work adds no bay/tech load), over the same
  // committed tickets, so "% loaded" means the same thing on both surfaces.
  // Book time (getBookedMinutes) stays the raw sold/planned minutes.
  const techById = new Map((technicians || []).map((t: any) => [t.id, t]));
  const getEffectiveMinutes = (date: Date) =>
    sumEffectiveWorkload(workloadFor(date).tickets, techById, clock.nowMs());

  // Mechanical repair capacity only (work roles): a detail-only specialist's
  // hours are not repair capacity; TECHNICIAN + DETAIL still counts.
  const mechanicalTechnicianIds = useMechanicalTechnicianIds(activeShop?.id);
  const mechanicalRoster = useMechanicalRoster(activeShop?.id);
  const capacityInputsReady = rosterState.status === 'ready'
    && rosterState.shopId === activeShop?.id
    && mechanicalRoster.status === 'known'
    && absenceIntervals.status === 'known';
  const mechanicalTechs = (technicians || []).filter((t: any) => countsAsMechanicalCapacity(t, mechanicalTechnicianIds));

  // ===================== OBLI (OPTIBAY LOAD INDEX) HELPERS =====================

  // Specialty jobs and waiters on the day's committed workload.
  const getSpecialtyCounts = (date: Date) => {
    const w = workloadFor(date);
    return { specialtyCount: w.specialtyCount, waiterCount: w.waiterCount, totalJobs: w.ticketCount };
  };

  // Who works this day: the canonical schedule resolver (numeric AND day-name
  // hour keys, time off). The Calendar read numeric keys only, so a
  // technician whose hours are keyed by day name was silently left out.
  const getTechsWorkingOnDate = (date: Date) => {
    if (!isShopOpen(date)) return [];
    return mechanicalTechs.filter((tech) => {
      const c = resolveTechnicianCapacity(tech, date, 'scheduled');
      return c.state === 'known' && c.minutes > 0;
    });
  };

  // CORE-AW §44: known capacity only — the aggregate resolves every
  // technician itself and reports unknown hours explicitly.
  const calculateShopCapacity = (date: Date) =>
    aggregateShopCapacity(isShopOpen(date) ? technicians : [], date, mechanicalTechnicianIds, 'scheduled');

  const calculateTotalCapacity = (date: Date) => calculateShopCapacity(date).minutes;

  // Booked minutes: the committed workload's predicted minutes.
  const getBookedMinutes = (date: Date) => workloadFor(date).bookedMinutes;

  /**
   * Calculates the OBLI (OptiBay Load Index) for a given date using shared utility.
   */
  const calculateOBLIResult = (date: Date): OBLIResult => {
    const { specialtyCount, waiterCount, totalJobs } = getSpecialtyCounts(date);
    const shopCapacity = calculateShopCapacity(date);
    const workingTechs = getTechsWorkingOnDate(date);
    const totalCapacity = calculateTotalCapacity(date);
    // OBLI reads effective load (the Dayboard's number), not raw book time.
    const effectiveMinutes = getEffectiveMinutes(date);

    return calculateOBLIScore({
      capacityKnown: isOBLICapacityKnown({
        rosterLoaded: capacityInputsReady,
        mechanicalRosterKnown: capacityInputsReady,
        knownCapacityMinutes: shopCapacity.minutes,
        unknownHoursCount: shopCapacity.unknownCount,
      }),
      totalCapacity,
      bookedMinutes: effectiveMinutes,
      ticketCount: totalJobs,
      waiterCount,
      specialtyJobCount: specialtyCount,
      workingTechCount: workingTechs.length,
      totalTechCount: mechanicalTechs.length,
      isShopOpen: isShopOpen(date)
    });
  };

  // ===================== VISUAL INDICATOR HELPERS =====================

  const getWorkloadStatusColor = (date: Date) => {
    const totalCapacity = calculateTotalCapacity(date);
    const ticketCount = getTicketCount(date);
    
    // No capacity configured
    if (totalCapacity === 0) {
      return 'bg-muted'; // Grey/neutral
    }
    
    // No tickets - light workload
    if (ticketCount === 0) {
      return 'bg-green-500';
    }
    
    const { specialtyCount, totalJobs } = getSpecialtyCounts(date);
    const utilization = calculateShopUtilization(date);
    
    if (totalJobs > 0) {
      const specialtyRatio = (specialtyCount / totalJobs) * 100;
      if (specialtyRatio > 40) return 'bg-purple-500';
    }
    
    if (utilization > 80) return 'bg-red-500';
    if (utilization > 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTicketCountBadgeColor = (count: number) => {
    if (count > 20) return 'bg-red-500/90';
    if (count >= 10) return 'bg-yellow-500/90';
    return 'bg-green-500/90';
  };

  const getOBLIDotColor = (score: number) => {
    if (score === 0) return 'bg-muted'; // Grey for no capacity or closed
    if (score <= 25) return 'bg-green-500'; // 0-25: Easy/low stress
    if (score <= 50) return 'bg-yellow-500'; // 26-50: Moderate stress
    if (score <= 75) return 'bg-orange-500'; // 51-75: High stress
    return 'bg-red-500'; // 76-100: Severe stress
  };

  const renderCapacityBar = (date: Date) => {
    const totalCapacity = calculateTotalCapacity(date);
    const percentage = calculateShopUtilization(date, false);
    
    const fillWidth = Math.min(100, percentage);
    const isOverbooked = percentage > 100;
    
    return (
      <div className="w-full h-0.5 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn(
            "h-full transition-all",
            isOverbooked ? "bg-red-500" : "bg-primary"
          )}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
    );
  };

  // ===================== DAILY SUMMARY COMPONENT =====================

  const DailySchedulingSummary = ({ date }: { date: Date }) => {
    const ticketCount = getTicketCount(date);
    const totalCapacity = calculateTotalCapacity(date);
    // Booked Hours shows raw book time (sold/planned); Capacity Used is effective load.
    const bookedMinutes = getBookedMinutes(date);
    const utilizationPct = calculateShopUtilization(date, false);
    const { specialtyCount } = getSpecialtyCounts(date);
    const obliResult = calculateOBLIResult(date);
    const obliScore = obliResult.score;
    const obliUnavailable = obliResult.state === 'unavailable';

    // The same committed workload the badge, capacity and OBLI use.
    const dayTickets = workloadFor(date).tickets;

    const waiterCount = dayTickets.filter(t => t.type === 'Waiter').length;
    const dropoffCount = dayTickets.filter(t => t.type === 'Drop-off').length;

    const earliestTime = dayTickets.length > 0
      ? format(new Date(Math.min(...dayTickets.map(t => new Date(t.start_time).getTime()))), 'h:mm a')
      : 'N/A';
    
    const latestTime = dayTickets.length > 0
      ? format(new Date(Math.max(...dayTickets.map(t => new Date(t.start_time).getTime()))), 'h:mm a')
      : 'N/A';

    return (
      <div className="space-y-3 min-w-[280px]">
        <div className="border-b pb-2">
          <h3 className="font-semibold text-sm">
            {format(date, 'EEEE, MMM d, yyyy')}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div>
            <span className="text-muted-foreground">Total Tickets:</span>
          </div>
          <div className="font-medium text-right">{ticketCount}</div>

          <div>
            <span className="text-muted-foreground">Capacity Used:</span>
          </div>
          <div className="font-medium text-right">
            {totalCapacity === 0 ? '–' : `${utilizationPct.toFixed(0)}%`}
          </div>

          <div>
            <span className="text-muted-foreground">Booked Hours:</span>
          </div>
          <div className="font-medium text-right">{(bookedMinutes / 60).toFixed(1)}h</div>

          <div>
            <span className="text-muted-foreground">Total Capacity:</span>
          </div>
          <div className="font-medium text-right">
            {totalCapacity === 0 ? (
              <span className="text-destructive">No capacity</span>
            ) : (
              `${(totalCapacity / 60).toFixed(1)}h`
            )}
          </div>

          <div>
            <span className="text-muted-foreground">Waiters:</span>
          </div>
          <div className="font-medium text-right">{waiterCount}</div>

          <div>
            <span className="text-muted-foreground">Drop-offs:</span>
          </div>
          <div className="font-medium text-right">{dropoffCount}</div>

          <div>
            <span className="text-muted-foreground">Specialty Jobs:</span>
          </div>
          <div className="font-medium text-right">{specialtyCount}</div>

          <div>
            <span className="text-muted-foreground">Earliest Job:</span>
          </div>
          <div className="font-medium text-right">{earliestTime}</div>

          <div>
            <span className="text-muted-foreground">Latest Job:</span>
          </div>
          <div className="font-medium text-right">{latestTime}</div>
        </div>

        <div className="border-t pt-2 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">OBLI (OptiBay Load Index):</span>
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", obliUnavailable ? 'bg-muted' : getOBLIDotColor(obliScore))} />
              <span className="font-bold" data-testid="calendar-obli-score" data-obli-state={obliResult.state}>
                {obliUnavailable ? `${formatOBLIScore(obliResult)} · ${OBLI_CAPACITY_UNAVAILABLE_LABEL}` : `${obliScore}/100`}
              </span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground leading-tight">
            OBLI (OptiBay Load Index) shows how heavy the day is. 0 = light/smooth day. 100 = heavy/overloaded day.
          </p>
        </div>
      </div>
    );
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(viewMonth),
    end: endOfMonth(viewMonth)
  });

  const firstDayOfWeek = getDay(startOfMonth(viewMonth));
  const paddingDays = Array(firstDayOfWeek).fill(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{format(viewMonth, 'MMMM yyyy')}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMonth(subMonths(viewMonth, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </DialogTitle>
          <p className="text-sm text-muted-foreground text-center pt-2">
            Choose a date to view schedule
          </p>
        </DialogHeader>

        {/* Core-100 R4 — CORE-BR: shops with outdoor bays see the weather notice where they schedule. */}
        <SchedulingWeatherAdvisory shopId={activeShop?.id} />

        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-semibold p-2">
              {day}
            </div>
          ))}
          
          {paddingDays.map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          
          {daysInMonth.map(date => {
            const isOpen = isShopOpen(date);
            const ticketCount = getTicketCount(date);
            const obliResult = calculateOBLIResult(date);
            const obliScore = obliResult.score;
            const obliUnavailable = obliResult.state === 'unavailable';
            const status = dayStatus(date);
            const isToday = status.isToday;
            const isSelected = isSameDay(date, currentDate);

            if (!isOpen) {
              return (
                <div
                  key={date.toISOString()}
                  data-testid={status.kind === 'holiday' ? 'calendar-day-holiday' : 'calendar-day-closed'}
                  className={cn(
                    "relative min-h-[80px] p-2 rounded border border-muted bg-muted/50 flex items-center justify-center text-center",
                    isToday && "border-primary border-2",
                  )}
                >
                  <span className="text-xs text-muted-foreground">
                    {status.kind === 'holiday' ? status.label : 'Closed'}
                  </span>
                </div>
              );
            }

            return (
              <HoverCard key={date.toISOString()} openDelay={200}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      // Emit YYYY-MM-DD string directly, not a Date object
                      const dayLabel = format(date, 'yyyy-MM-dd');
                      onDateSelect(dayLabel);
                      onOpenChange(false);
                      // Auto-advance tutorial when selecting today's date on calendar step
                      if (isActive && currentStepData?.id === 'calendar-modal' && isToday) {
                        nextStep();
                      }
                    }}
                    className={cn(
                      "relative min-h-[80px] p-2 rounded border transition-all hover:border-primary hover:shadow-sm",
                      isSelected && "border-primary bg-primary/10",
                      isToday && "border-primary border-2",
                      !isSelected && !isToday && "border-border bg-card",
                      // Core-100 R2 CORE-AX-GAP-001: a forward day is a projection, drawn as one.
                      status.temporal === 'future' && !isSelected && "border-dashed",
                      "flex flex-col justify-between"
                    )}
                    data-temporal={status.temporal}
                    aria-description={status.temporal === 'future' ? PROJECTION_NOTE : status.temporal === 'past' ? PAST_DAY_NOTE : undefined}
                  >
                    {/* Top Section: Workload Dot + Ticket Badge */}
                    <div className="flex items-start justify-between w-full">
                      <div 
                        className={cn("w-2 h-2 rounded-full", getWorkloadStatusColor(date))}
                        title={`Utilization: ${calculateShopUtilization(date).toFixed(0)}%`}
                      />
                      {ticketCount > 0 && (
                        <div 
                          className={cn(
                            "text-[10px] font-medium text-white px-1.5 py-0.5 rounded-full",
                            getTicketCountBadgeColor(ticketCount)
                          )}
                        >
                          {ticketCount}
                        </div>
                      )}
                    </div>

                    {/* Center Section: Date Number */}
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <span className="text-sm font-medium">{format(date, 'd')}</span>
                      {status.temporal === 'future' && (
                        <span className="text-[9px] text-muted-foreground" data-testid="calendar-day-projected">projected</span>
                      )}
                      {status.temporal === 'past' && (
                        <span className="text-[9px] text-muted-foreground" data-testid="calendar-day-recomputed">recomputed</span>
                      )}
                    </div>

                    {/* Bottom Section: OBLI Dot + Capacity Bar */}
                    <div className="space-y-1 w-full">
                      <div className="flex justify-end">
                        <div 
                          className={cn("w-2 h-2 rounded-full", obliUnavailable ? 'bg-muted' : getOBLIDotColor(obliScore))}
                          title={obliUnavailable ? `OBLI (OptiBay Load Index): ${OBLI_CAPACITY_UNAVAILABLE_LABEL}` : `OBLI (OptiBay Load Index): ${obliScore}`}
                        />
                      </div>
                      {renderCapacityBar(date)}
                    </div>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent align="start" className="w-auto p-3">
                  {status.temporal === 'future' && (
                    <p className="text-[11px] text-muted-foreground mb-2 max-w-[16rem]" data-testid="calendar-projection-note">{PROJECTION_NOTE}</p>
                  )}
                  {status.temporal === 'past' && (
                    <p className="text-[11px] text-muted-foreground mb-2 max-w-[16rem]" data-testid="calendar-past-note">{PAST_DAY_NOTE}</p>
                  )}
                  <DailySchedulingSummary date={date} />
                </HoverCardContent>
              </HoverCard>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};